import { Typography } from '@statsbomb/kitbag-components';

import {
  CompetitionSelector,
  DateRangeSelector,
  FilterScopeProvider,
  PlayerSelector,
  SeasonSelector,
  TeamSelector,
} from '@statsbomb/kitbag-global-football';
import { useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import {
  competitionOptionsAtom,
  defaultCompetitionSectionCycleAtom,
  haveCompetitionOptionsLoadedAtom,
} from '@/atoms/filters/highLevel/competitions';
import { playerOptionsAtom } from '@/atoms/filters/highLevel/players';
import { haveSeasonOptionsLoadedAtom, seasonOptionsAtom } from '@/atoms/filters/highLevel/seasons';
import { teamOptionsAtom } from '@/atoms/filters/highLevel/teams';
import { isPopulatingPlayerCacheAtom } from '@/atoms/queries/players';
import { usePhysicalMetricsFeature } from '@/hooks/featureFlags';

const unwrappedCompetitionOptionsAtom = unwrap(competitionOptionsAtom, prev => prev ?? []);
const unwrappedSeasonOptionsAtom = unwrap(seasonOptionsAtom, prev => prev ?? []);
const unwrappedTeamOptionsAtom = unwrap(teamOptionsAtom, prev => prev ?? []);
const unwrappedCyclesAtom = unwrap(defaultCompetitionSectionCycleAtom, prev => prev ?? []);
const unwrappedPlayerOptionsAtom = unwrap(playerOptionsAtom, prev => prev ?? null);
const unwrappedIsPopulatingPlayerCacheAtom = unwrap(isPopulatingPlayerCacheAtom, prev => prev ?? true);

const PlayerDataLockerGeneralFiltersContent = () => {
  const { t } = useTranslation('filters');
  const competitionOptions = useAtomValue(unwrappedCompetitionOptionsAtom);
  const seasonOptions = useAtomValue(unwrappedSeasonOptionsAtom);
  const teamOptions = useAtomValue(unwrappedTeamOptionsAtom);
  const cycles = useAtomValue(unwrappedCyclesAtom);
  const playerOptions = useAtomValue(unwrappedPlayerOptionsAtom);
  const isPopulatingPlayerCache = useAtomValue(unwrappedIsPopulatingPlayerCacheAtom);
  const haveCompetitionOptionsLoaded = useAtomValue(haveCompetitionOptionsLoadedAtom);
  const haveSeasonOptionsLoaded = useAtomValue(haveSeasonOptionsLoadedAtom);
  const physicalMetricsEnabled = usePhysicalMetricsFeature();

  const playersLoading =
    playerOptions === null || isPopulatingPlayerCache || !haveCompetitionOptionsLoaded || !haveSeasonOptionsLoaded;

  return (
    <>
      <CompetitionSelector options={competitionOptions} playerCycles={cycles} showTracking={physicalMetricsEnabled} />
      <SeasonSelector options={seasonOptions} reDefaultOnOptionsChange={false} />
      <TeamSelector options={teamOptions} />
      <PlayerSelector options={playerOptions ?? []} isLoading={playersLoading} />
      <Typography variant="headingRegular">{t('dateRange')}</Typography>
      <DateRangeSelector />
    </>
  );
};

export const PlayerDataLockerGeneralFilters = () => (
  <FilterScopeProvider scope="main">
    <PlayerDataLockerGeneralFiltersContent />
  </FilterScopeProvider>
);
